import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import IconButton from "@material-ui/core/IconButton";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch } from "react-redux";
import { setAnalyticsDetails } from "../../../features/layers/overlaylayerslice";
import { ToastContainer, toast } from 'react-toastify';

const Timeline = ({ data, fulldates }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const intervalTime = 5000;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate steps with optimized display for mobile
  const generateSteps = () => {
    const steps = [];
    let previousDate = null;
    fulldates.forEach((date, dateIndex) => {
      // For mobile, only show marks at 6-hour intervals
      const interval = isMobile ? 6 : 3;
      
      for (let hour = 0; hour < 24; hour += interval) {
        const time = `${hour.toString().padStart(2, "0")}:00`;
        // For mobile, only show date label for the first time entry of each date
        const label = (date !== previousDate && (isMobile ? hour === 3 : true)) ? date : "";
        steps.push({ date, time, label });
        previousDate = date;
      }
    });
    return steps;
  };

  const steps = generateSteps();

  const togglePlayPause = () => {
    setIsPlaying((prevState) => !prevState);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < steps.length - 1) {
      interval = setInterval(() => {
        setCurrentStep((prevStep) => {
          const nextStep = prevStep + 1;
          if (nextStep >= steps.length) {
            setIsPlaying(false);
            return prevStep;
          }
          dispatch(
            setAnalyticsDetails({
              id: data.id - 1,
              date: formatDateTime(steps[nextStep].date, steps[nextStep].time),
              isPlaying: true,
            })
          );
          return nextStep;
        });
      }, intervalTime);
    } else if (isPlaying && currentStep >= steps.length - 1) {
      // Reset to beginning if at end and still playing
      setCurrentStep(0);
      dispatch(
        setAnalyticsDetails({
          id: data.id - 1,
          date: formatDateTime(steps[0].date, steps[0].time),
          isPlaying: true,
        })
      );
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, steps.length, dispatch, data.id, steps]);

  const formatDateTime = (date, time) => {
    const [year, month, day] = date.split('-');
    const [hour, minute] = time.split(':');
    return `${year}-${month}-${day}-hr-${hour}-${minute}-00`;
  };

  const handleStepChange = (e) => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const newStep = Math.round(percent * (steps.length - 1));
    
    setCurrentStep(newStep);
    setIsPlaying(false);
    dispatch(setAnalyticsDetails({ 
      id: data.id - 1, 
      date: formatDateTime(steps[newStep].date, steps[newStep].time),
      isPlaying: false, 
    }));
  };

  const handleMouseMove = (e) => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const step = Math.round(percent * (steps.length - 1));
    
    setHoveredStep(step);
  };

  return (
    <TimelineWrapper ref={containerRef}>
      <ControlsContainer>
        <PlayPauseButton 
          $isMobile={isMobile}
          onClick={togglePlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </PlayPauseButton>
        
        <SliderContainer $isMobile={isMobile}>
          <SliderTrack 
            ref={sliderRef}
            onClick={handleStepChange}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredStep(null)}
            $isMobile={isMobile}
          >
            <SliderProgress style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }} />
            
            {/* Current position tooltip - only show on desktop */}
            {!isMobile && (
              <Tooltip
                open={true}
                title={
                  <div>
                    <div>Date: {steps[currentStep].date}</div>
                    <div>Time: {steps[currentStep].time}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <SliderThumb 
                  style={{ left: `${(currentStep / (steps.length - 1)) * 100}%` }} 
                  $isMobile={isMobile}
                />
              </Tooltip>
            )}
            
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {(!isMobile || index % 2 === 0) && ( // Show fewer marks on mobile
                  <SliderMark 
                    style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                    onMouseEnter={() => setHoveredStep(index)}
                    $isMobile={isMobile}
                  />
                )}
                {step.label && (
                  <SliderLabel 
                    style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                    $isMobile={isMobile}
                  >
                    {isMobile ? formatMobileDate(step.date) : step.date}
                  </SliderLabel>
                )}
              </React.Fragment>
            ))}
            
            {!isMobile && hoveredStep !== null && hoveredStep !== currentStep && (
              <Tooltip
                open={true}
                title={
                  <div>
                    <div>Date: {steps[hoveredStep].date}</div>
                    <div>Time: {steps[hoveredStep].time}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <HoverIndicator style={{ left: `${(hoveredStep / (steps.length - 1)) * 100}%` }} />
              </Tooltip>
            )}
          </SliderTrack>
          
          {/* Mobile current time display */}
          {isMobile && (
            <MobileTimeDisplay>
              {steps[currentStep].date} {steps[currentStep].time}
            </MobileTimeDisplay>
          )}
        </SliderContainer>
      </ControlsContainer>
      
      <ToastContainer position="bottom-left" autoClose={3000} />
    </TimelineWrapper>
  );
};

// Helper function to format date for mobile display
const formatMobileDate = (date) => {
  const [year, month, day] = date.split('-');
  return `${month}/${day}`;
};

export default Timeline;

// Styled Components with improved responsiveness
const TimelineWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  color: white;
  border-radius: 8px;
  position: fixed;
  z-index: 999;
  bottom: 30px;
  left: 10px;
  right: 10px;
  background: rgba(3, 57, 105, 0.66);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: calc(100% - 20px);
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (min-width: 480px) {
    padding: 10px 15px;
    bottom: 15px;
  }

  @media (min-width: 768px) {
    left: 20px;
    right: 20px;
    bottom: 20px;
    max-width: calc(100% - 40px);
    padding: 12px 20px;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;

  @media (min-width: 480px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    gap: 25px;
  }
`;

const PlayPauseButton = styled(IconButton)`
  color: #ff5722;
  width: ${props => props.$isMobile ? '32px' : '40px'};
  height: ${props => props.$isMobile ? '32px' : '40px'};
  background-color: white !important;
  border-radius: 50%;
  flex-shrink: 0;
  padding: 0;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    background-color: white;
  }

  & svg {
    font-size: ${props => props.$isMobile ? '20px' : '24px'};
    transition: font-size 0.2s ease;
  }
`;

const SliderContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding: ${props => props.$isMobile ? '5px 0' : '10px 0'};
  position: relative;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$isMobile ? '3px' : '4px'};
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
`;

const SliderProgress = styled.div`
  position: absolute;
  height: 100%;
  background: #ff5722;
  border-radius: 2px;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '12px' : '14px'};
  height: ${props => props.$isMobile ? '12px' : '14px'};
  background: #ff5722;
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
`;

const SliderMark = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '4px' : '6px'};
  height: ${props => props.$isMobile ? '4px' : '6px'};
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 1;
`;

const SliderLabel = styled.div`
  position: absolute;
  top: ${props => props.$isMobile ? '8px' : '12px'};
  transform: translateX(-50%);
  font-size: ${props => props.$isMobile ? '9px' : '10px'};
  color: white;
  white-space: nowrap;
  font-weight: bold;
  z-index: 1;

  @media (min-width: 480px) {
    font-size: ${props => props.$isMobile ? '10px' : '11px'};
    top: ${props => props.$isMobile ? '10px' : '15px'};
  }
`;

const HoverIndicator = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`;

const MobileTimeDisplay = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  text-align: center;
  width: 100%;
`;